import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Typography, Autocomplete, Avatar, Icon } from "@ann/web-components";
import { ModalContent, SearchUsersWrapper, ToBeAddedWrapper, TotallyToAddWrapper, ToBeAddedList, ToBeAddedItem, ToBeAddedItemLeft, ToBeAddedItemRight, ActionsWrapper } from "./AssignRoleModal.styled";
import { getUserList, assignRole } from "../../services";
import { useToast } from "../../../../../components/toast";

const AssignRoleModal = ({ 
  isOpen, 
  onClose, 
  roleUuid = "",
  roleName = "",
  onAssign 
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const searchTimeoutRef = useRef(null);
  const toast = useToast();

  // Fetch users with debounce
  const fetchUsers = useCallback(async (search) => {
    setIsLoading(true);
    try {
      const response = await getUserList({
        page: 1,
        page_size: 20,
        search: search || "",
      });
      
      const userList = response.body || [];
      setUsers(userList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchUsers(searchValue);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue, isOpen, fetchUsers]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUsers([]);
      setSelectedUsers([]);
      setSearchValue("");
    }
  }, [isOpen]);

  const handleClose = () => {
    setSelectedUsers([]);
    setSearchValue("");
    onClose();
  };

  const handleAssign = async () => {
    if (selectedUsers.length === 0 || !roleUuid) return;

    try {
      setIsAssigning(true);

      // Programación defensiva: soportar ambos formatos (uuid y userUuid)
      const userUuids = selectedUsers.map(user => user.uuid || user.userUuid);
      const roleUuids = [roleUuid];

      await assignRole(userUuids, roleUuids);

      toast.success({
        message: `Role assigned successfully to ${selectedUsers.length} ${selectedUsers.length === 1 ? 'user' : 'users'}`,
        canClose: true
      });

      if (onAssign) {
        onAssign({
          success: true,
          users: selectedUsers,
        });
      }

      handleClose();
    } catch (error) {
      console.error("Error assigning role:", error);

      toast.error({
        message: error.message || "Error assigning role",
        canClose: true
      });

      if (onAssign) {
        onAssign({
          success: false,
          error: error,
        });
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const handleUserSelect = (suggestion) => {
    // Find the user by the suggestion label (fullName or email)
    const user = users.find(u => 
      (u.fullName || u.email || "Unknown User") === suggestion
    );
    
    // Programación defensiva: soportar ambos formatos (uuid y userUuid)
    if (user) {
      const userId = user.uuid || user.userUuid;
      const isAlreadySelected = selectedUsers.find(u => {
        const selectedUserId = u.uuid || u.userUuid;
        return selectedUserId === userId;
      });
      
      if (!isAlreadySelected) {
        setSelectedUsers([...selectedUsers, user]);
      }
    }
    setSearchValue("");
  };

  const handleRemoveUser = (userUuid) => {
    // Programación defensiva: soportar ambos formatos (uuid y userUuid)
    setSelectedUsers(prev => prev.filter(user => {
      const userId = user.uuid || user.userUuid;
      return userId !== userUuid;
    }));
  };

  // Map users to Autocomplete suggestions format (array of strings)
  const userSuggestions = users
    .filter(user => {
      // Programación defensiva: soportar ambos formatos (uuid y userUuid)
      const userId = user.uuid || user.userUuid;
      return !selectedUsers.find(selected => {
        const selectedUserId = selected.uuid || selected.userUuid;
        return selectedUserId === userId;
      });
    })
    .map(user => user.fullName || user.email || "Unknown User");

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>
        <Modal.Header.Title>Assign Role</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <Typography category="body" size="medium" weight="regular" color="body">
            {roleName}
          </Typography>
          <SearchUsersWrapper id="search-users-wrapper">
            <Autocomplete
              placeholder="Search by name or email..."
              value={searchValue}
              onChange={handleSearchChange}
              onSearch={() => {}}
              onSuggestionSelect={handleUserSelect}
              onClear={() => setSearchValue("")}
              suggestions={userSuggestions}
            />
          </SearchUsersWrapper>
          <ToBeAddedWrapper $hasUsers={selectedUsers.length > 0}>
            <TotallyToAddWrapper>
              <Typography category="subtitle" size="small" weight="bold">
                To be added
              </Typography>
              <Typography category="body" size="medium" weight="regular" color="body">
                You're about to add {selectedUsers.length} {selectedUsers.length === 1 ? 'user' : 'users'}
              </Typography>
            </TotallyToAddWrapper>
            <ToBeAddedList>
                {selectedUsers.map((user) => {
                  // Programación defensiva: soportar ambos formatos (uuid y userUuid)
                  const userId = user.uuid || user.userUuid;
                  return (
                    <ToBeAddedItem key={userId}>
                      <ToBeAddedItemLeft>
                        <Avatar alt="Default avatar" size="md" src="https://placehold.co/16x16"/>
                        <Typography category="body" size="medium" weight="regular" color="body">
                          {user.fullName}
                        </Typography>
                      </ToBeAddedItemLeft>
                      <ToBeAddedItemRight onClick={() => handleRemoveUser(userId)}>
                        <Icon name="close" size="medium" />
                      </ToBeAddedItemRight>
                    </ToBeAddedItem>
                  );
                })}
              </ToBeAddedList>
          </ToBeAddedWrapper>
        </ModalContent>
      </Modal.Body>
      <ActionsWrapper $hasUsers={selectedUsers.length > 0}>
        <Modal.Actions align="end">
          <Modal.Button
            label="Cancel"
            onClick={handleClose}
            type="secondary"
            disabled={isAssigning}
          />
          <Modal.Button
            label={isAssigning ? "Assigning..." : "Assign role"}
            onClick={handleAssign}
            disabled={isAssigning}
          />
        </Modal.Actions>
      </ActionsWrapper>
    </Modal>
  );
};

export default AssignRoleModal;

